import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import { getAssetTree } from '../common/apis'
import LiveSelect from 'live/components/forms/ui/select'
import { capitalize, cloneDeep } from 'lodash'
import ActionToggle from 'live/components/ui/action-toggle'
interface IProps {
    lineProducts: { productName: string; skus: string[] }[]
    setLineProducts: Dispatch<SetStateAction<{ productName: string; skus: string[] }[]>>
}

export function LineProductSelect({ lineProducts, setLineProducts }: IProps): JSX.Element {
    const [AvailabelProducts, setAvailabelProducts] = useState<SelectedInputObj[]>([])
    const [SkuInfo, setSkuInfo] = useState({})
    const [selectedProducts, setSelectedProducts] = useState<SelectedInputObj[]>([])
    const [productTree, setProductTree] = useState<ProductData[]>([])

    useEffect(() => {
        getAssetTree('product')
            .then(result => {
                setProductTree(result as ProductData[])

                return result.map(product => convertObjToValidSelectObj(product))
            })
            .then(options => {
                setAvailabelProducts(options)
                const { configuredProducts, configuredSkus } = getCurrentLineConfigs()
                if (configuredProducts.length) {
                    setSelectedProducts(configuredProducts)
                    setSkuInfo(configuredSkus)
                }
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (lineProducts.length) {
            const { configuredProducts } = getCurrentLineConfigs()
            if (configuredProducts.length) {
                setSelectedProducts(configuredProducts)
                const productNames = configuredProducts.map(el => el.value)

                setSkuInfo(getSkusFromProducts(productNames))
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productTree])

    function getCurrentLineConfigs(): {
        configuredProducts: SelectedInputObj[]
        configuredSkus: Record<string, string[]>
    } {
        return lineProducts.reduce(
            (acc, product) => {
                acc.configuredProducts.push(convertObjToValidSelectObj(product))

                acc.configuredSkus[product.productName] = product.skus

                return acc
            },
            { configuredProducts: [], configuredSkus: {} }
        )
    }

    function convertObjToValidSelectObj(obj: { productName: string }): SelectedInputObj {
        return {
            value: obj.productName,
            label: obj.productName
        }
    }

    function onSelectProduct(selectedProductsArray: SelectedInputObj[]): void {
        setSelectedProducts(selectedProductsArray)
        const productNames = selectedProductsArray.map(el => el.value)

        setSkuInfo(getSkusFromProducts(productNames))
        setLineProducts(getLineProducts(productNames))
    }

    function getSkusFromProducts(productNames: string[]): Record<string, string[]> {
        return productTree.reduce((acc, product) => {
            if (productNames.includes(product.productName)) {
                acc[product.productName] = product.SKUs.map(sku => sku.skuName)
            }

            return acc
        }, {})
    }

    function getLineProducts(productNames: string[]): { productName: string; skus: string[] }[] {
        return productNames.reduce((acc, name) => {
            const currentProductObje = lineProducts.filter(product => product.productName === name)
            const newObj = { productName: name, skus: currentProductObje[0]?.skus || [] }
            acc.push(newObj)

            return acc
        }, [] as { productName: string; skus: string[] }[])
    }

    function handleSkuClick(productName: string, skuName: string, isActive: boolean, indexOfProduct: number): void {
        const newLineProducts = cloneDeep(lineProducts)
        if (isActive) {
            newLineProducts[indexOfProduct].skus = newLineProducts[indexOfProduct].skus.filter(sku => sku !== skuName)
        } else {
            newLineProducts[indexOfProduct].skus.push(skuName)
        }

        setLineProducts(newLineProducts)
    }

    function checkIfSkuIsActive(productName: string, skuName: string): { isActive: boolean; indexOfProduct: number } {
        return lineProducts.reduce(
            (acc, product, index) => {
                if (acc.indexOfProduct >= 0) return acc

                if (productName === product.productName) {
                    acc.indexOfProduct = index
                    if (product.skus.includes(skuName)) acc.isActive = true
                }

                return acc
            },
            { isActive: false as boolean, indexOfProduct: -1 }
        )
    }

    return (
        <StyledLineProductSelect>
            <LiveSelect
                placeholder={i18n('choose the products that belong to this line')}
                value={selectedProducts}
                multi={true}
                closeOnSelect={false}
                removeSelected={true}
                clearable={false}
                searchable={true}
                options={AvailabelProducts}
                onChange={(value, obj) => onSelectProduct(obj)}
            />

            {selectedProducts.length === 0 ? (
                <div className={'lineProductConfigs'}>
                    <div className="lineProductConfigs--noContent">
                        <span className="lineProductConfigs--noContent__icon">
                            <i className="fas fa-empty-set"></i>
                        </span>
                        <div className="lineProductConfigs--noContent__noSku">{i18n('There are no SKUs')}</div>
                        <i className="lineProductConfigs--noContent__text">
                            {i18n('Choose a product to configure SKUs')}
                        </i>
                    </div>
                </div>
            ) : (
                <div className={'lineProductConfigs'}>
                    {selectedProducts.map((product, index) => (
                        <div key={product.value} className={'lineProductConfigs--product'}>
                            <div className="lineProductConfigs--product__titles">
                                <span className="lineProductConfigs--product__titles--name">
                                    {product.label.toUpperCase()}
                                </span>
                                {index ? null : (
                                    <span className="lineProductConfigs--product__titles--status">Status</span>
                                )}
                            </div>
                            {SkuInfo[product.value]?.map(skuName => {
                                const { isActive, indexOfProduct } = checkIfSkuIsActive(product.value, skuName)

                                return (
                                    <div key={skuName} className="lineProductConfigs--product__sku">
                                        <span className="lineProductConfigs--product__sku--name">
                                            {skuName.toUpperCase()}
                                        </span>
                                        <ActionToggle
                                            className={'centered'}
                                            active={isActive}
                                            onChange={() =>
                                                handleSkuClick(product.value, skuName, isActive, indexOfProduct)
                                            }
                                        />
                                    </div>
                                )
                            })}
                        </div>
                    ))}
                </div>
            )}
        </StyledLineProductSelect>
    )
}

const StyledLineProductSelect = styled.div`
    .lineProductConfigs {
        background: ${({ theme }) => theme['gray-100']};
        border: 1px solid ${({ theme }) => theme['gray-400']};
        display: flex;
        flex-direction: column;
        width: 100%;
        padding: 16px;
        border-radius: 3px;
        margin-bottom: 24px;
        &--product {
            width: 100%;
            display: flex;
            flex-direction: column;
            margin-bottom: 15px;
            &:last-child {
                margin: 0;
            }
            &__titles,
            &__sku {
                display: flex;
                justify-content: space-between;
                align-items: center;
                color: ${({ theme }) => theme['gray-700']};
                font-family: ${({ theme }) => theme['font-semibold']};
                padding: 5px 0;
                &--name {
                    width: 88%;
                    font-size: 13px;
                }
                &--status {
                    text-align: center;
                    margin-right: 9px;
                }
                a {
                    margin: 0;
                }
            }
            &__titles {
                &--name,
                &--status {
                    color: ${({ theme }) => theme['gray-800']};
                    font-size: 14px;
                }
            }
        }
        &--noContent {
            margin-left: auto;
            margin-right: auto;
            text-align: center;

            &__icon {
                color: ${({ theme }) => theme['gray-500']};
                font-size: 24px;
            }
            &__noSku {
                font-size: 13px;
                font-family: ${({ theme }) => theme['font-semibold']};
                line-height: 16px;
            }
            &__text {
                font-size: 13px;
                font-family: ${({ theme }) => theme['font-semibold']};
                color: #868695;
            }
        }
    }
`
